<template>
  <floating-element
    ref="el"
    class="context-menu"
    :transition-name="transition"
    @contextmenu.prevent=""
    @click="onClick($event)"
    @entered="onTransitionEntered"
    @pointerdown:outside="close($event)"
  >
    <slot></slot>
  </floating-element>
</template>

<script setup lang="ts">
import { ref } from "vue";

import FloatingElement from "./FloatingElement.vue";

interface Props {
  transition?: string;
  closeOnClick?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  transition: "transition-from-left-corner",
  closeOnClick: true
});
const emit = defineEmits<{
  (e: "close", mouseEvent: MouseEvent): void;
}>();

const dialogElement = ref<InstanceType<typeof FloatingElement>>();

function onClick(event: MouseEvent) {
  if (event.defaultPrevented) return;
  props.closeOnClick ? close(event) : dontClose();
}

function close(event: MouseEvent) {
  emit("close", event);
}

function onTransitionEntered() {
  if (dialogElement.value && dialogElement.value.el) {
    // focus first item
    const firstFocussableItem = dialogElement.value.el.querySelector<
      | HTMLButtonElement
      | HTMLInputElement
      | HTMLTextAreaElement
      | HTMLSelectElement
    >(":is(button, input, textarea, select)");
    if (firstFocussableItem) {
      firstFocussableItem.focus();
    }
  }
}

function dontClose() {
  console.log("not closing contextmenu");
}
</script>

<style lang="scss">
.context-menu {
  width: 16rem;
  position: fixed;
  background-color: var(--color-background-close);
  padding: 0.25em 0;
  border-radius: 5px;
  box-shadow: 0 2px 1em var(--color-shadow-2);
  transition: transform 50ms ease-out;
  max-height: 100%;
  overflow: auto;

  &__entry {
    display: block;
    width: 100%;
    padding: 0.5em;
    border: none;
    background-color: var(--color-background-close);
    color: var(--color-text);
    font-weight: 400;
    font-size: 13px;
    font-family: var(--font-family-ui);
    text-align: left;

    &--danger {
      color: var(--color-danger-text);
      font-weight: 600;
    }

    &:focus-visible {
      color: var(--color-action-contrast);
      background-color: var(--color-action);
    }

    &:hover {
      color: var(--color-action-contrast);
      background-color: var(--color-action);
    }

    &:disabled {
      color: var(--color-text-light);
      &:hover {
        background-color: var(--color-background-close);
      }
    }
  }

  &__label {
    display: block;
    padding-inline: 0.5em;
    padding-block: 0.5rem;
    font-weight: 600;
    margin: 0;
  }

  hr {
    margin-block: 0.5rem;
  }
}
</style>
